import React from "react";
import PropTypes from 'prop-types';
import { graphql } from "gatsby";
import Layout from "../components/Layout";
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';
import { OPEN_CONSENT_EVENT } from '../components/CookieConsent';
import legalLinks from '../data/legalLinks';
import locales from '../constants/locales';

const texts = {
  fr: {
    eyebrow: 'Informations légales',
    updated: 'Mis à jour le',
    documents: 'Documents',
    onThisPage: 'Sur cette page',
    labels: { notice: 'Mentions légales', privacy: 'Confidentialité', terms: 'Conditions d’utilisation', cookies: 'Cookies' },
    manageTitle: 'Vos préférences',
    manageText: 'Consultez ou modifiez à tout moment les catégories de cookies que vous acceptez.',
    manageButton: 'Gérer mes cookies',
    contactTitle: 'Une question ?',
    contactText: 'Pour toute question sur ces informations ou pour exercer vos droits, écrivez-nous.',
  },
  en: {
    eyebrow: 'Legal information',
    updated: 'Updated on',
    documents: 'Documents',
    onThisPage: 'On this page',
    labels: { notice: 'Legal notice', privacy: 'Privacy', terms: 'Terms of use', cookies: 'Cookies' },
    manageTitle: 'Your preferences',
    manageText: 'Review or change the cookie categories you accept at any time.',
    manageButton: 'Manage my cookies',
    contactTitle: 'Any questions?',
    contactText: 'For any question about this information or to exercise your rights, write to us.',
  },
};

const slugify = (text) => text
  .replace(/<[^>]+>/g, '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

// Give every h2 an id and build the table of contents
const withAnchors = (html) => {
  const toc = [];
  const out = html.replace(/<h2>([\s\S]*?)<\/h2>/g, (match, inner) => {
    const id = slugify(inner);
    toc.push({ id, label: inner.replace(/<[^>]+>/g, '') });
    return `<h2 id="${id}">${inner}</h2>`;
  });
  return { html: out, toc };
};

const LegalPost = ({ pageContext: { locale }, data }) => {
  const post = data.markdownRemark;
  const fm = post.frontmatter;
  const lang = locale === 'en' ? 'en' : 'fr';
  const t = texts[lang];
  const links = legalLinks[lang];
  const current = Object.keys(links).find((key) => links[key] === `/${fm.slug}/`);
  const { html, toc } = withAnchors(post.html);

  return (
    <Layout locale={locale}>
      <SEO
        title={`TriDyme | ${fm.title}`}
        lang={locale}
        url={locales[locale].default
          ? `/${fm.slug}/`
          : `/${locales[locale].path}/${fm.slug}/`}
        description={fm.description}
        author={fm.author}
      />
      <div className="legal-page">
        <header className="legal-hero">
          <div className="legal-hero-inner">
            <span className="legal-eyebrow">{t.eyebrow}</span>
            <h1>{fm.title}</h1>
            {fm.description && <p>{fm.description}</p>}
            <span className="legal-updated">{t.updated} {lang === 'fr' ? fm.dateFr : fm.dateEn}</span>
          </div>
        </header>

        <div className="legal-layout">
          <aside className="legal-aside">
            <nav aria-label={t.documents}>
              <span className="legal-aside-title">{t.documents}</span>
              <ul>
                {Object.keys(links).map((key) => (
                  <li key={key}>
                    <LocalizedLink
                      to={links[key]}
                      className={key === current ? 'active' : ''}
                      aria-current={key === current ? 'page' : undefined}
                    >
                      {t.labels[key]}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </nav>
            {toc.length > 1 && (
              <nav aria-label={t.onThisPage} className="legal-toc">
                <span className="legal-aside-title">{t.onThisPage}</span>
                <ul>
                  {toc.map((item) => (
                    <li key={item.id}><a href={`#${item.id}`}>{item.label}</a></li>
                  ))}
                </ul>
              </nav>
            )}
          </aside>

          <div className="legal-main">
            {current === 'cookies' && (
              <div className="legal-card legal-card-accent">
                <div>
                  <strong>{t.manageTitle}</strong>
                  <p>{t.manageText}</p>
                </div>
                <button
                  type="button"
                  className="cookie-btn"
                  onClick={() => window.dispatchEvent(new Event(OPEN_CONSENT_EVENT))}
                >
                  {t.manageButton}
                </button>
              </div>
            )}
            <article className="legal-content" dangerouslySetInnerHTML={{ __html: html }} />
            <div className="legal-card">
              <div>
                <strong>{t.contactTitle}</strong>
                <p>{t.contactText}</p>
              </div>
              <a className="cookie-btn cookie-btn-ghost" href="mailto:contact@tridyme.com">contact@tridyme.com</a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

LegalPost.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired
}

export default LegalPost;

export const query = graphql`
  query($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      frontmatter {
        slug
        title
        dateFr: date(formatString: "D MMMM YYYY", locale: "fr")
        dateEn: date(formatString: "D MMMM YYYY", locale: "en")
        author
        language
        description
      }
      fields {
        slug
      }
    }
  }
`
